import Vue from 'vue'
import {ActionTree, GetterTree, Module, MutationTree} from 'vuex';
import {RootState} from '../RootState';
import axios from "axios";
import {settings} from "@/plugins/settings";
import {IErrors} from "@/model/IErrors";
import {IShortUser} from "@/model/shortInterfaces";
import {ICommunicationMessage} from "@/model/Communication/ICommunicationMessage";
import responseHandler from "@/plugins/errorHandling";
import {IUser, IUserAddress, IUserMgm} from "@/model/User/UserInterface";
import { IUserLog } from '@/model/logs/Ilogs';
import {IOrder} from "@/model/Order/orderInterfaces";
import {
  PAGINATION_FIRST_PAGE,
  PAGINATION_DEFAULT_LAST_PAGE,
  PAGINATION_DEFAULT_TOTAL,
  PAGINATION_DEFAULT_PER_PAGE,
  PAGINATION_NO_NEXT_PAGE,
} from "@/constants/pagination";

export interface IAuditLog {
  id: number;
  action: string;
  old_data: Record<string, any> | null;
  new_data: Record<string, any> | null;
  user_id: number | null;
  changed_by_name: string | null;
  ip: string | null;
  created_at: string;
}

interface UserState {
  user?: IUser;
  shortUser?: IShortUser;
  userList: IShortUser[];
  userSearch: IShortUser[],
  activeUserSearch: IShortUser[],
  lockerOwners: IShortUser[],
  // user?: IShortUser;
  nextPageToLoad: number;
  currentPage: number;
  lastPage: number;
  total: number;
  perPage: number;
  communications: ICommunicationMessage[];
  logs: IUserLog[];
  auditLogs: IAuditLog[];
  orders: IOrder[];
  mgm: IUserMgm;
  addresses: IUserAddress[];
  defaultBillingAddressId: number | null;
  defaultDeliveryAddressId: number | null;
  countries: any[];
  dashboard: any;
  errors: IErrors;
}

const state: UserState = {
  user: undefined,
  shortUser: undefined,
  userList: [],
  userSearch: [],
  activeUserSearch: [],
  lockerOwners: [],
  nextPageToLoad: PAGINATION_FIRST_PAGE,
  currentPage: PAGINATION_FIRST_PAGE,
  lastPage: PAGINATION_DEFAULT_LAST_PAGE,
  total: PAGINATION_DEFAULT_TOTAL,
  perPage: PAGINATION_DEFAULT_PER_PAGE,
  communications: [],
  logs: [],
  auditLogs: [],
  orders: [],
  mgm: { invitedBy: null, usersInvited: [] },
  addresses: [],
  defaultBillingAddressId: null,
  defaultDeliveryAddressId: null,
  countries: [],
  dashboard: null,
  errors: {},
};

const getters: GetterTree<UserState, RootState> = {
  shortUser: (state): IShortUser => state.shortUser || {} as IShortUser,
  user: (state): IShortUser => state.user || {} as IUser,
  userList: (state): IShortUser[] => state.userList,
  userSearch: (state): IShortUser[] => state.userSearch,
  activeUserSearch: (state): IShortUser[] => state.activeUserSearch,
  lockerOwners: (state): IShortUser[] => state.lockerOwners,
  // user: (state): IShortUser => state.user || {} as IShortUser,
  canLoadNextPage: (state): boolean => state.nextPageToLoad !== PAGINATION_NO_NEXT_PAGE,
  currentPage: (state): number => state.currentPage,
  lastPage: (state): number => state.lastPage,
  total: (state): number => state.total,
  perPage: (state): number => state.perPage,
  communications: (state) => state.communications,
  logs: (state): IUserLog[] => state.logs,
  auditLogs: (state): IAuditLog[] => state.auditLogs,
  orders: (state): IOrder[] => state.orders,
  mgm: (state): IUserMgm => state.mgm,
  addresses: (state): IUserAddress[] => state.addresses,
  defaultBillingAddressId: (state): number | null => state.defaultBillingAddressId,
  defaultDeliveryAddressId: (state): number | null => state.defaultDeliveryAddressId,
  countries: (state): any[] => state.countries,
  dashboard: (state): any => state.dashboard,
  errors: (state) => state.errors,
};

const actions: ActionTree<UserState, RootState> = {
  setLockerCompany: ({ commit }, {user_id, locker_company_id}): any => responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'users/' + user_id + '/lockerCompany/' + locker_company_id,
        method: 'PUT'
      }),
      commit,
      'lockerCompanySet'
  ),
  migrate: ({ commit }, {userId, lockerId}): any => responseHandler.handle(
      () => axios({
        url: settings.endpoint + 'users/' + userId + '/locker/' + lockerId,
        method: 'PUT'
      }),
      commit,
      'userMigrated'
  ),
  fetchShortUser: ({commit}, userId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/short',
    }),
    commit,
    'shortUserLoaded'
  ),
  fetchUser: ({commit}, userId): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId,
    }),
    commit,
    'userLoaded'
  ),
  resetUser: ({commit}): any => commit('resetUser'),
  resendPasswordChangeEmail: ({commit}, userId): any => responseHandler.handle(
    () => axios({
      url: `${ settings.endpoint }users/${userId}/sendResetPasswordMail`,// settings.endpoint + 'users/' + userId + '/sendResetMail',
      method: 'POST',
    }),
    commit,
    'emailSent',
  ),
  clearUsers: ({commit}) => commit('clearUsers'),
  fetchUsers: ({commit}, filters): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users',
      method: 'GET',
      params: filters
        }),
    commit,
    'usersLoaded',
  ),
  fetchUserSearch: ({commit}, search): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users',
      method: 'GET',
      params: {search}
    }),
    commit,
    'userSearchLoaded'
  ),
  fetchActiveUserSearch: ({commit}, search): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users',
      method: 'GET',
      params: {search, active_only: 1}
    }),
    commit,
    'activeUserSearchLoaded'
  ),
  clearActiveUserSearch: ({commit}): any => commit('activeUserSearchLoaded', []),
  clearUserSearch: ({commit}): any => commit('userSearchLoaded', []),
  fetchLockerOwners: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/locker-owners',
      method: 'GET',
    }),
    commit,
    'lockerOwnersLoaded'
  ),
  fetchCommunications: ({commit}, filters: {userId: number}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + filters.userId + '/communications',
      method: 'GET'
    }),
    commit,
    'fetchCommunications'
  ),
  fetchLogs: ({commit}: any, {userId, filters}: any): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/logs',
      params: {
        ...filters,
      },
      method: 'GET'
    }),
    commit,
    'fetchLogs'
  ),
  clearLogs:  ({commit}): any => {
    commit('clearLogs');
  },
  fetchAuditLogs: ({commit}: any, {userId, params}: any): Promise<any> => {
    return axios({
      url: settings.endpoint + 'users/' + userId + '/audit-logs',
      params: params || {},
      method: 'GET',
    }).then((response: any) => {
      const payload = response && response.data && response.data.data ? response.data.data : {};
      commit('fetchAuditLogs', Array.isArray(payload.data) ? payload.data : []);
      return payload;
    });
  },
  clearAuditLogs: ({commit}): any => {
    commit('clearAuditLogs');
  },
  fetchOrders:  ({ commit }, filters): any => {
    return responseHandler.handle(
        () => axios({
          url: settings.endpoint + 'orders',
          params: {
            ...filters,
          },
        }).then((response) => {
          const paginated = response.data && response.data.data;
          if (paginated && !Array.isArray(paginated) && Array.isArray(paginated.data)) {
            response.data.data = paginated.data;
          }
          return response;
        }),
        commit,
        'ordersLoaded'
    );
  },
  clearOrders: ({ commit }) => {
    commit('clearOrders');
  },
  errors: ({commit}, errors: IErrors): any => {
    commit('errors', errors);
  },
  cleanError: ({commit}, index: any): any => {
    commit('cleanError', index);
  },
  updateUser: ({commit}, {userId, data}: {userId: number, data: any}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId,
      method: 'PATCH',
      data,
    }),
    commit,
    'userUpdated'
  ),
  deleteUser: ({commit}, {userId, confirm}: {userId: number, confirm: string}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId,
      method: 'DELETE',
      data: { confirm },
    }),
    commit,
    'userDeleted'
  ),
  regenerateDeliveryToken: ({commit}, {userId}: {userId: number}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/delivery-token',
      method: 'POST',
    }),
    commit,
    'deliveryTokenRegenerated'
  ),
  fetchDashboard: ({commit}, userId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/dashboard',
    }),
    commit,
    'dashboardLoaded'
  ),
  fetchMgm: ({commit}, userId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/mgm',
    }),
    commit,
    'mgmLoaded'
  ),
  fetchAddresses: ({commit}, userId: number): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/addresses',
    }),
    commit,
    'addressesLoaded'
  ),
  createAddress: ({commit}, {userId, data}: {userId: number, data: any}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/addresses',
      method: 'POST',
      data,
    }),
    commit,
    'addressCreated'
  ),
  updateAddress: ({commit}, {userId, addressId, data}: {userId: number, addressId: number, data: any}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/addresses/' + addressId,
      method: 'PATCH',
      data,
    }),
    commit,
    'addressUpdated'
  ),
  fetchCountries: ({commit}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/countries',
    }),
    commit,
    'countriesLoaded'
  ),
  deleteAddress: ({commit}, {userId, addressId}: {userId: number, addressId: number}): any => responseHandler.handle(
    () => axios({
      url: settings.endpoint + 'users/' + userId + '/addresses/' + addressId,
      method: 'DELETE',
    }),
    commit,
    'addressDeleted'
  ),
};

const mutations: MutationTree<UserState> = {
  lockerCompanySet(state, payload) {
    state.user = payload;
  },
  userMigrated(state, payload) {
    state.user = payload;
  },
  shortUserLoaded(state, payload: IShortUser) {
    state.shortUser = payload;
  },
  userLoaded(state, payload: IUser) {
    state.user = payload;
  },
  resetUser(state) {
    state.user = {} as IUser;
  },
  clearUsers(state) {
    state.userList = [];
    state.currentPage = PAGINATION_FIRST_PAGE;
    state.lastPage = PAGINATION_DEFAULT_LAST_PAGE;
    state.total = PAGINATION_DEFAULT_TOTAL;
    state.nextPageToLoad = PAGINATION_FIRST_PAGE;
  },
  usersLoaded(state, payload: any) {
    let userData: any[] = [];
    let currentPage = PAGINATION_FIRST_PAGE;
    let lastPage = PAGINATION_DEFAULT_LAST_PAGE;
    let total = PAGINATION_DEFAULT_TOTAL;
    let perPage = PAGINATION_DEFAULT_PER_PAGE;

    if (!payload) {
      state.userList = [];
      return;
    }

    if (payload.data !== undefined && Array.isArray(payload.data)) {
      userData = payload.data;
      currentPage = payload.current_page || PAGINATION_FIRST_PAGE;
      lastPage = payload.last_page || PAGINATION_DEFAULT_LAST_PAGE;
      total = payload.total || PAGINATION_DEFAULT_TOTAL;
      perPage = payload.per_page || PAGINATION_DEFAULT_PER_PAGE;
    }
    else if (Array.isArray(payload)) {
      userData = payload;
    }

    state.userList = userData;
    state.currentPage = currentPage;
    state.lastPage = lastPage;
    state.total = total;
    state.perPage = perPage;
    state.nextPageToLoad = currentPage < lastPage ? currentPage + 1 : PAGINATION_NO_NEXT_PAGE;
  },
  userSearchLoaded(state, payload) {
    state.userSearch = Array.isArray(payload) ? payload : (payload.data || []);
  },
  activeUserSearchLoaded(state, payload) {
    state.activeUserSearch = Array.isArray(payload) ? payload : (payload.data || []);
  },
  lockerOwnersLoaded(state, payload) {
    state.lockerOwners = Array.isArray(payload) ? payload : (payload.data || []);
  },
  fetchCommunications(state, payload) {
    state.communications = payload;
  },
  fetchLogs(state, payload) {
    state.logs = state.logs.concat(payload);
  },
  clearLogs(state) {
    state.logs = [];
  },
  fetchAuditLogs(state, payload) {
    state.auditLogs = Array.isArray(payload) ? payload : [];
  },
  clearAuditLogs(state) {
    state.auditLogs = [];
  },
  clearOrders(state) {
    state.orders = [];
  },
  ordersLoaded(state, payload: any) {
    const items: IOrder[] = Array.isArray(payload) ? payload : (Array.isArray(payload && payload.data) ? payload.data : []);
    state.orders = state.orders.concat(items);
  },
  errors(state, payload: IErrors) {
    state.errors = payload;
  },
  cleanError(state, index: string) {
    Vue.delete(state.errors, index);
  },
  userUpdated(state, payload: IUser) {
    state.user = payload;
  },
  userDeleted(state) {
    state.user = {} as IUser;
  },
  deliveryTokenRegenerated(state, payload: { delivery_token: string }) {
    if (state.user) {
      state.user.delivery_token = payload.delivery_token;
    }
  },
  dashboardLoaded(state, payload: any) {
    state.dashboard = payload;
  },
  mgmLoaded(state, payload: IUserMgm) {
    state.mgm = payload;
  },
  addressesLoaded(state, payload: any) {
    state.addresses = payload.addresses || [];
    state.defaultBillingAddressId = payload.default_billing_address_id || null;
    state.defaultDeliveryAddressId = payload.default_delivery_address_id || null;
  },
  addressCreated(state, payload: IUserAddress) {
    state.addresses.push(payload);
  },
  addressUpdated(state, payload: IUserAddress) {
    const idx = state.addresses.findIndex(a => a.id === payload.id);
    if (idx !== -1) {
      Vue.set(state.addresses, idx, payload);
    }
  },
  addressDeleted(state) {
    // will be reloaded by the component
  },
  countriesLoaded(state, payload: any) {
    state.countries = Array.isArray(payload) ? payload : (payload.data || payload);
  },
};

const namespaced: boolean = true;

export const userModule: Module<UserState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations
};
